<template>
  <transition name="fadeIn">
    <div class="popup promocode-modal" v-if="show" @click="closeModal">
      <div class="popup__window popup__window--wide" @click.stop="">
        <div class="popup__inner">
          <div class="popup__title">
            {{ isEdit ? 'Редактирование промокода' : 'Создание промокода' }}
          </div>

          <form class="promocode-form" @submit.prevent="onSubmit">
            <!-- Основные параметры -->
            <div class="form-section">
              <div class="form-section__title">Основные параметры</div>

              <div class="form__item" :class="{ 'form__item--error': $v.code.$error, 'form__item--filled': code }">
                <span class="promocode__auto" @click="generateCode" title="Сгенерировать код">
                  <img src="@/assets/images/icons/update.svg" alt="update">
                </span>
                <input
                  class="form__input"
                  id="code"
                  v-model.lazy="code"
                  :class="{error: $v.code.$error}"
                  :disabled="isEdit && promocode.usersCount > 0"
                  @change="$v.code.$touch()"
                  autocomplete="off"
                >
                <label for="code" class="form__label">
                  <span>Код промокода</span>
                </label>
                <p class="form__item-error" v-if="!$v.code.required">Обязательное поле</p>
                <p class="form__item-error" v-if="!$v.code.minLength || !$v.code.maxLength || !$v.code.alpha">
                  Код должен быть длиной 3-8 символов (латиница и цифры)
                </p>
                <p class="form__item-hint" v-if="isEdit && promocode.usersCount > 0">
                  Код нельзя изменить, т.к. есть активации
                </p>
              </div>

              <div class="form__row">
                <div class="form__item" :class="{ 'form__item--error': $v.dateStart.$error, 'form__item--filled': dateStart }">
                  <input
                    class="form__input"
                    id="dateStart"
                    v-model="dateStart"
                    :class="{error: $v.dateStart.$error}"
                    @change="$v.dateStart.$touch()"
                    v-mask="'##.##.####'"
                    autocomplete="off"
                  >
                  <label for="dateStart" class="form__label"><span>Дата начала</span></label>
                  <p class="form__item-error" v-if="!$v.dateStart.required">Обязательное поле</p>
                  <p class="form__item-error" v-if="!$v.dateStart.minLength || !$v.dateStart.maxLength">Введите дату</p>
                </div>
                <div class="form__item" :class="{ 'form__item--error': $v.timeStart.$error, 'form__item--filled': timeStart }">
                  <input
                    class="form__input"
                    id="timeStart"
                    v-model="timeStart"
                    :class="{error: $v.timeStart.$error}"
                    @change="$v.timeStart.$touch()"
                    v-mask="'##:##'"
                    autocomplete="off"
                  >
                  <label for="timeStart" class="form__label"><span>Время начала</span></label>
                  <p class="form__item-error" v-if="!$v.timeStart.required">Обязательное поле</p>
                  <p class="form__item-error" v-if="!$v.timeStart.timeType">Введите время</p>
                </div>
              </div>

              <div class="form__row">
                <div class="form__item" :class="{ 'form__item--error': $v.dateEnd.$error, 'form__item--filled': dateEnd }">
                  <input
                    class="form__input"
                    id="dateEnd"
                    v-model="dateEnd"
                    :class="{error: $v.dateEnd.$error}"
                    @change="$v.dateEnd.$touch()"
                    v-mask="'##.##.####'"
                    autocomplete="off"
                  >
                  <label for="dateEnd" class="form__label"><span>Дата окончания</span></label>
                  <p class="form__item-error" v-if="!$v.dateEnd.required">Обязательное поле</p>
                  <p class="form__item-error" v-if="!$v.dateEnd.minLength || !$v.dateEnd.maxLength">Введите дату</p>
                </div>
                <div class="form__item" :class="{ 'form__item--error': $v.timeEnd.$error, 'form__item--filled': timeEnd }">
                  <input
                    class="form__input"
                    id="timeEnd"
                    v-model="timeEnd"
                    :class="{error: $v.timeEnd.$error}"
                    @change="$v.timeEnd.$touch()"
                    v-mask="'##:##'"
                    autocomplete="off"
                  >
                  <label for="timeEnd" class="form__label"><span>Время окончания</span></label>
                  <p class="form__item-error" v-if="!$v.timeEnd.required">Обязательное поле</p>
                  <p class="form__item-error" v-if="!$v.timeEnd.timeType">Введите время</p>
                </div>
              </div>

              <div class="form__item" :class="{ 'form__item--filled': amount !== null && amount !== '' }">
                <input
                  class="form__input"
                  id="amount"
                  v-model.number="amount"
                  type="number"
                  min="0"
                  autocomplete="off"
                >
                <label for="amount" class="form__label"><span>Сумма скидки (руб.)</span></label>
                <p class="form__item-hint">Оставьте пустым, если не влияет на цену</p>
              </div>
            </div>

            <!-- Дополнительные параметры -->
            <div class="form-section form-section--collapsible" :class="{'form-section--open': showAdvanced}">
              <div class="form-section__title form-section__title--clickable" @click="showAdvanced = !showAdvanced">
                <span>Дополнительные параметры</span>
                <svg class="form-section__toggle" :class="{'form-section__toggle--open': showAdvanced}" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6L8 10L12 6" stroke="#8A8C96" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>

              <div class="form-section__content" v-show="showAdvanced">
                <div class="form__row">
                  <div class="form__item form__item--select">
                    <label for="period" class="form__select-label">Период подписки</label>
                    <select class="form__select" id="period" v-model="period">
                      <option value="DAY">День</option>
                      <option value="WEEK">Неделя</option>
                      <option value="MONTH">Месяц</option>
                      <option value="LIFETIME">Бессрочная</option>
                    </select>
                  </div>
                  <div class="form__item" :class="{ 'form__item--filled': periodsCount }">
                    <input
                      class="form__input"
                      id="periodsCount"
                      v-model.number="periodsCount"
                      type="number"
                      min="1"
                      autocomplete="off"
                    >
                    <label for="periodsCount" class="form__label"><span>Кол-во периодов</span></label>
                  </div>
                </div>

                <div class="form__item form__item--select">
                  <label for="userOrganizationType" class="form__select-label">Тип организации</label>
                  <select class="form__select" id="userOrganizationType" v-model="userOrganizationType">
                    <option :value="null">Все типы организаций</option>
                    <option value="LLC">ООО</option>
                    <option value="JSC">АО</option>
                    <option value="INDIVIDUAL_ENTREPRENEUR">ИП</option>
                    <option value="INDIVIDUAL">Физлицо</option>
                    <option value="SELF_EMPLOYED">Самозанятый</option>
                  </select>
                </div>

                <div class="form__checkbox-group">
                  <label class="form__checkbox-wrapper">
                    <input
                      type="checkbox"
                      id="isPersonal"
                      v-model="isPersonal"
                      class="form__checkbox-input"
                    >
                    <span class="form__checkbox-box"></span>
                    <span class="form__checkbox-text">Персональный промокод</span>
                  </label>

                  <label class="form__checkbox-wrapper">
                    <input
                      type="checkbox"
                      id="hasTrialPeriod"
                      v-model="hasTrialPeriod"
                      class="form__checkbox-input"
                    >
                    <span class="form__checkbox-box"></span>
                    <span class="form__checkbox-text">С пробным периодом</span>
                  </label>
                </div>

                <div class="form__row" v-if="hasTrialPeriod">
                  <div class="form__item" :class="{ 'form__item--filled': trialPeriodDays }">
                    <input
                      class="form__input"
                      id="trialPeriodDays"
                      v-model.number="trialPeriodDays"
                      type="number"
                      min="0"
                      autocomplete="off"
                    >
                    <label for="trialPeriodDays" class="form__label"><span>Дней триала</span></label>
                  </div>
                  <div class="form__item" :class="{ 'form__item--filled': trialAmount }">
                    <input
                      class="form__input"
                      id="trialAmount"
                      v-model.number="trialAmount"
                      type="number"
                      min="0"
                      autocomplete="off"
                    >
                    <label for="trialAmount" class="form__label"><span>Сумма за триал (руб.)</span></label>
                  </div>
                </div>
              </div>
            </div>

            <div class="popup__controlls" :class="{'form-send': isSending}">
              <Button type="submit">{{ isEdit ? 'Сохранить' : 'Создать' }}</Button>
              <Button :class="'btn--grey'" type="button" @click="closeModal">Отмена</Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import Button from '@/components/UI/Button'

export default {
  name: 'PromocodeModal',
  components: {
    Button,
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    promocode: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      code: '',
      dateStart: '',
      dateEnd: '',
      timeStart: '',
      timeEnd: '',
      amount: null,
      period: 'MONTH',
      periodsCount: 1,
      userOrganizationType: null,
      isPersonal: false,
      hasTrialPeriod: false,
      trialPeriodDays: 0,
      trialAmount: 0,
      showAdvanced: false,
      isSending: false,
    }
  },
  computed: {
    isEdit() {
      return !!this.promocode
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.promocode) {
        this.populateForm()
      } else if (newVal) {
        this.resetForm()
      }
    },
    promocode(newVal) {
      if (newVal && this.show) {
        this.populateForm()
      }
    }
  },
  validations: {
    code: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(8),
      alpha: val => /^[a-zA-Z0-9]*$/i.test(val),
    },
    dateStart: {
      required,
      minLength: minLength(10),
      maxLength: maxLength(10),
    },
    dateEnd: {
      required,
      minLength: minLength(10),
      maxLength: maxLength(10),
    },
    timeStart: {
      required,
      minLength: minLength(5),
      maxLength: maxLength(5),
      timeType(val) {
        if (!val || val.length < 5) return true
        let timeArray = val.split(':')
        return timeArray[0] < 24 && timeArray[1] < 60
      },
    },
    timeEnd: {
      required,
      minLength: minLength(5),
      maxLength: maxLength(5),
      timeType(val) {
        if (!val || val.length < 5) return true
        let timeArray = val.split(':')
        return timeArray[0] < 24 && timeArray[1] < 60
      },
    }
  },
  methods: {
    populateForm() {
      const p = this.promocode
      this.code = p.code || ''
      this.dateStart = this.formatDateForInput(p.startDt)
      this.timeStart = this.formatTimeForInput(p.startDt)
      this.dateEnd = this.formatDateForInput(p.endDt)
      this.timeEnd = this.formatTimeForInput(p.endDt)
      this.amount = p.amount
      this.period = p.period || 'MONTH'
      this.periodsCount = p.periodsCount || 1
      this.userOrganizationType = p.userOrganizationType
      this.isPersonal = p.isPersonal || false
      this.hasTrialPeriod = p.hasTrialPeriod || false
      this.trialPeriodDays = p.trialPeriodDays || 0
      this.trialAmount = p.trialAmount || 0
      this.showAdvanced = this.hasTrialPeriod || this.isPersonal || this.userOrganizationType
    },
    resetForm() {
      this.code = ''
      this.dateStart = ''
      this.dateEnd = ''
      this.timeStart = ''
      this.timeEnd = ''
      this.amount = null
      this.period = 'MONTH'
      this.periodsCount = 1
      this.userOrganizationType = null
      this.isPersonal = false
      this.hasTrialPeriod = false
      this.trialPeriodDays = 0
      this.trialAmount = 0
      this.showAdvanced = false
      this.$v.$reset()
    },
    formatDateForInput(timestamp) {
      if (!timestamp) return ''
      const date = new Date(+timestamp)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}.${month}.${year}`
    },
    formatTimeForInput(timestamp) {
      if (!timestamp) return ''
      const date = new Date(+timestamp)
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    formatDateToTimestamp(date, time) {
      let dateArray = date.split('.')
      let dateString = `${dateArray[1]} ${dateArray[0]}, ${dateArray[2]} ${time}:00`
      return new Date(dateString).getTime()
    },
    async generateCode() {
      this.isSending = true
      this.code = await this.$store.dispatch('autoPromocode')
      this.isSending = false
    },
    closeModal() {
      this.$emit('close')
    },
    async onSubmit() {
      this.$v.$touch()
      if (this.$v.$invalid) return

      const promocodeInfo = {
        code: this.code,
        startDt: this.formatDateToTimestamp(this.dateStart, this.timeStart),
        endDt: this.formatDateToTimestamp(this.dateEnd, this.timeEnd),
        amount: this.amount || null,
        period: this.period,
        periodsCount: this.periodsCount,
        userOrganizationType: this.userOrganizationType,
        isPersonal: this.isPersonal,
        hasTrialPeriod: this.hasTrialPeriod,
        trialPeriodDays: this.hasTrialPeriod ? this.trialPeriodDays : 0,
        trialAmount: this.hasTrialPeriod ? this.trialAmount : 0,
      }

      this.isSending = true
      try {
        if (this.isEdit) {
          await this.$store.dispatch('updatePromocode', {
            promocodeId: this.promocode.id,
            promocodeInfo
          })
        } else {
          await this.$store.dispatch('createPromocode', promocodeInfo)
        }
        this.$emit('saved')
        this.closeModal()
      } catch (e) {
        // ошибка уже обработана в store
      } finally {
        this.isSending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.promocode-modal {
  .popup__window--wide {
    max-width: 56rem;
    width: 100%;
  }

  .popup__inner {
    padding: 2.4rem;
  }

  .popup__title {
    margin-bottom: 2rem;
    font-size: 1.8rem;
    font-weight: 600;
  }
}

.promocode-form {
  .form-section {
    margin-bottom: 2rem;
    padding: 1.6rem;
    background: #F8F9FA;
    border-radius: 1rem;

    &__title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #191B1D;
      margin-bottom: 1.4rem;

      &--clickable {
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0;
        user-select: none;

        &:hover {
          color: #008069;
        }
      }
    }

    &__toggle {
      transition: transform 0.2s;

      &--open {
        transform: rotate(180deg);
      }
    }

    &__content {
      margin-top: 1.4rem;
    }

    &--collapsible {
      .form-section__title {
        margin-bottom: 0;
      }
    }
  }

  .form__row {
    display: flex;
    align-items: flex-end;
    gap: 1.2rem;
    margin-top: 1.2rem;

    .form__item {
      flex: 1;
      margin-top: 0;
    }

    .form__item--select {
      margin-top: 0;
    }
  }

  .form__item {
    position: relative;
    margin-top: 1.2rem;

    &:first-child {
      margin-top: 0;
    }

    &--error {
      .form__input {
        border-color: #C62828;
      }
    }

    &--select {
      margin-top: 1.2rem;
    }
  }

  .form__input {
    width: 100%;
    height: 5rem;
    padding: 0 3rem 0 1.2rem;
    font-size: 1.4rem;
    border: 1px solid #E0E0E0;
    border-radius: 0.8rem;
    background: #fff;
    transition: border-color 0.2s;
    outline: none;

    &:focus {
      border-color: #008069;
    }

    &:disabled {
      background-color: #F7F7FA;
      color: #8A8C96;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &[type=number] {
      -moz-appearance: textfield;
    }
  }

  .form__label {
    position: absolute;
    left: 1.6rem;
    top: 1.5rem;
    font-size: 1.4rem;
    color: #8A8C96;
    pointer-events: none;
    transition: all 0.15s ease;

    span {
      background: #fff;
      padding: 0 0.2rem;
      border-radius: 10px;
    }
  }

  .form__input:focus + .form__label,
  .form__item--filled .form__label {
    top: -1.6rem;
    left: -1rem;
    font-size: 1.1rem;
    color: #008069;
  }

  /* Select стили */
  .form__select {
    width: 100%;
    height: 5rem;
    padding: 0 3rem 0 1.2rem;
    font-size: 1.4rem;
    border: 1px solid #E0E0E0;
    border-radius: 0.8rem;
    background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8C96' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat right 1.2rem center;
    appearance: none;
    cursor: pointer;
    outline: none;

    &:focus {
      border-color: #008069;
    }
  }

  .form__select-label {
    display: block;
    font-size: 1.1rem;
    color: #8A8C96;
    margin-bottom: 0.4rem;
  }

  /* Checkbox стили */
  .form__checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1.4rem;
  }

  .form__checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
    user-select: none;
  }

  .form__checkbox-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .form__checkbox-box {
    width: 2rem;
    height: 2rem;
    border: 2px solid #E0E0E0;
    border-radius: 0.4rem;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;

    &::after {
      content: '';
      width: 1rem;
      height: 0.6rem;
      border: 2px solid #fff;
      border-top: none;
      border-right: none;
      transform: rotate(-45deg) scale(0);
      transition: transform 0.2s;
    }
  }

  .form__checkbox-input:checked + .form__checkbox-box {
    background: #008069;
    border-color: #008069;

    &::after {
      transform: rotate(-45deg) scale(1);
    }
  }

  .form__checkbox-input:focus + .form__checkbox-box {
    box-shadow: 0 0 0 3px rgba(0, 128, 105, 0.2);
  }

  .form__checkbox-text {
    font-size: 1.4rem;
    color: #191B1D;
  }

  .form__item-error {
    color: #C62828;
    font-size: 1.1rem;
    margin-top: 0.4rem;
  }

  .form__item-hint {
    color: #8A8C96;
    font-size: 1.1rem;
    margin-top: 0.4rem;
  }

  .promocode__auto {
    position: absolute;
    right: 1.2rem;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    z-index: 1;
    padding: 0.4rem;
    border-radius: 0.4rem;
    transition: background 0.2s;

    &:hover {
      background: #E8F5E9;
    }

    img {
      width: 1.8rem;
      height: 1.8rem;
      display: block;
    }
  }
}

.popup__controlls {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.2rem;
  margin-top: 2rem;
  justify-content: flex-end;

  .btn {
    margin: 0;
  }

  &.form-send {
    opacity: 0.5;
    pointer-events: none;
  }
}
</style>
